import React, { useEffect, useId, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

/**
 * LanguageSwitcher Component
 * Allows users to switch between English and Arabic
 * Automatically updates document direction (LTR/RTL)
 */
const LanguageSwitcher = ({ className = '', tone = 'light', inline = false }) => {
  const { i18n, t } = useTranslation();
  const [isOpen, setIsOpen] = useState(false);
  const buttonRef = useRef(null);
  const menuId = useId();

  const languages = [
    { code: 'en', nameKey: 'en', nativeName: 'English', flag: 'En' },
    { code: 'ar', nameKey: 'ar', nativeName: 'العربية', flag: '🇸🇦' }
  ];

  const resolvedLanguage = (i18n.resolvedLanguage || i18n.language || 'en').split('-')[0];
  const currentLanguage = languages.find(lang => lang.code === resolvedLanguage) || languages[0];
  const dark = tone === 'dark';

  useEffect(() => {
    if (!isOpen) return undefined;

    const handleKeyDown = (event) => {
      if (event.key === 'Escape') {
        event.preventDefault();
        event.stopImmediatePropagation();
        setIsOpen(false);
        buttonRef.current?.focus();
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [isOpen]);

  const changeLanguage = (langCode) => {
    i18n.changeLanguage(langCode);
    setIsOpen(false);
    buttonRef.current?.focus();

    // Update localStorage
    localStorage.setItem('i18nextLng', langCode);

    // Update document direction
    const dir = langCode === 'ar' ? 'rtl' : 'ltr';
    document.documentElement.setAttribute('dir', dir);
    document.documentElement.setAttribute('lang', langCode);
  };

  return (
    <div className={`relative ${inline ? 'w-full' : ''} ${className}`} data-language-switcher>
      <button
        ref={buttonRef}
        type="button"
        onClick={() => setIsOpen(!isOpen)}
        className={`flex min-h-11 items-center gap-2 rounded-md px-3 py-2 transition-colors ${
          dark ? 'text-white hover:bg-white/10' : 'text-[#17231f] hover:bg-[#e9f0ec]'
        }`}
        aria-label={t('common.language')}
        aria-expanded={isOpen}
        aria-controls={menuId}
      >
        <span className={currentLanguage.code === 'en' ? 'text-base font-semibold' : 'text-2xl'}>
          {currentLanguage.flag}
        </span>
        <span className="hidden font-medium md:inline-block">
          {currentLanguage.nativeName}
        </span>
        <svg
          className={`w-4 h-4 transition-transform ${isOpen ? 'rotate-180' : ''}`}
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
          aria-hidden="true"
        >
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
        </svg>
      </button>

      {isOpen && (
        <>
          {/* Backdrop */}
          <div
            className="fixed inset-0 z-10"
            onClick={() => setIsOpen(false)}
            aria-hidden="true"
          />

          {/* Dropdown Menu */}
          <div id={menuId} role="group" aria-label={t('common.language')} className={`${inline ? 'relative mt-2 w-full' : 'absolute end-0 mt-2 w-48'} z-20 overflow-hidden rounded-lg border border-gray-200 bg-white shadow-lg`}>
            {languages.map((language) => (
              <button
                key={language.code}
                type="button"
                aria-pressed={currentLanguage.code === language.code}
                onClick={() => changeLanguage(language.code)}
                className={`flex w-full items-center gap-3 px-4 py-3 transition-colors hover:bg-gray-50 ${
                  currentLanguage.code === language.code
                    ? 'bg-primary-50 text-primary-700'
                    : 'text-gray-700'
                }`}
              >
                <span
                  className={language.code === 'en' ? 'text-base font-semibold' : 'text-2xl'}
                  aria-hidden="true"
                >
                  {language.flag}
                </span>
                <div className="flex-1 text-start">
                  <div className="font-medium">{language.nativeName}</div>
                  <div className="text-xs text-gray-500">{t(`common.languages.${language.nameKey}`)}</div>
                </div>
                {currentLanguage.code === language.code && (
                  <svg className="h-5 w-5 text-primary-600" fill="currentColor" viewBox="0 0 20 20" aria-hidden="true">
                    <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                  </svg>
                )}
              </button>
            ))}
          </div>
        </>
      )}
    </div>
  );
};

export default LanguageSwitcher;
